import { queryOptions } from '@tanstack/react-query'
import { CACHE_TIMEOUT_MS } from 'stores/shippingStore/shipping.consts'
import {
  shippingOptionsManager,
  type DeliveryPromiseAddress,
  type DeliveryPromiseRequest,
} from 'services/ShippingOptionsManager'
import { mixtilesAxios } from 'utils/ApiUtils'

export const DELIVERY_PROMISES_QUERY_KEY = 'delivery-promises'

// Three attempts of 10s keep "calculating" to about 30s. Web responses take 1.4s at p99 and
// never over 10s, so a slower one is treated as failed.
const DELIVERY_PROMISE_ATTEMPT_TIMEOUT_MS = 10_000
const DELIVERY_PROMISE_RETRIES = 2

// customerEmail is an input because the backend picks the manufacturer partly by it
// (a customer's earlier orders, test accounts), so the same cart can get a different promise.
export interface DeliveryPromisesQueryInputs {
  address: DeliveryPromiseAddress | null
  deliveryPromiseRequests: DeliveryPromiseRequest[]
  variantsIds: string[]
  isTopQueueOn: boolean
  customerEmail: string | undefined
  pricingCountry: string
}

type ProductDeliveryPromiseQueryInputs = Omit<
  DeliveryPromisesQueryInputs,
  'deliveryPromiseRequests'
> & {
  deliveryPromiseRequest: DeliveryPromiseRequest
}

// One query per product, so a changed bag refetches only the products whose request changed.
export function getProductDeliveryPromiseQueryOptions(
  productDeliveryPromiseQueryInputs: ProductDeliveryPromiseQueryInputs
) {
  return queryOptions({
    queryKey: [DELIVERY_PROMISES_QUERY_KEY, productDeliveryPromiseQueryInputs],
    queryFn: () => {
      const { address, ...requestParams } = productDeliveryPromiseQueryInputs
      if (!address) {
        throw new Error('No address, query should not be enabled')
      }
      return shippingOptionsManager.fetchProductShippingOptions({
        address,
        ...requestParams,
        timeout: DELIVERY_PROMISE_ATTEMPT_TIMEOUT_MS,
      })
    },
    staleTime: CACHE_TIMEOUT_MS,
    // A 4xx (e.g. an address the backend rejects) fails the same way on every attempt.
    retry: (failureCount, error) =>
      !mixtilesAxios.shouldAbortRetry(error) &&
      failureCount < DELIVERY_PROMISE_RETRIES,
  })
}
